import { sendEmailVerification } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';

const EmailVerificationPrompt = ({ user, onSignOut, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const checkVerification = useCallback(async (showMessages = false) => {
    try {
      // Reload the user to get fresh email verification status
      await user.reload();
      if (user.emailVerified) {
        setMessage('Email verified! Redirecting...');
        // Trigger a refresh in the parent component
        setTimeout(() => onRefresh(), 500);
        return true;
      } else if (showMessages) {
        setError('Email not yet verified. Please check your inbox and click the verification link.');
      }
      return false;
    } catch (err) {
      console.error('Error checking verification:', err);
      if (showMessages) {
        setError('Failed to check verification status. Please try again.');
      }
      return false;
    }
  }, [user, onRefresh]);

  const handleResendEmail = async () => {
    setLoading(true);
    setMessage('');
    setError('');

    try {
      await sendEmailVerification(user);
      setMessage('Verification email sent! Please check your inbox.');
      // Set 60 second cooldown
      setResendCooldown(60);
    } catch (err) {
      console.error('Error sending verification email:', err);
      // Handle specific error codes
      if (err.code === 'auth/too-many-requests') {
        setError('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    setLoading(true);
    setMessage('');
    setError('');

    await checkVerification(true);

    setLoading(false);
  };

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Check on initial mount
  useEffect(() => {
    checkVerification(false);
  }, [checkVerification]);

  // Check when window gains focus (user returns from email tab)
  useEffect(() => {
    const handleFocus = () => {
      checkVerification(false);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkVerification]);

  // Listen for visibility changes (alternative to focus, catches more cases)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVerification(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkVerification]);

  return (
    <div className="min-h-screen bg-nova-bg flex items-center justify-center p-4">
      <div className="bg-gradient-card rounded-2xl shadow-2xl p-8 w-full max-w-md border border-nova-green/20">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-nova-green/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-nova-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Verify Your Email</h1>
          <p className="text-gray-400">
            We've sent a verification email to:
          </p>
          <p className="text-nova-green font-semibold mt-1">
            {user?.email}
          </p>
        </div>

        <div className="bg-nova-green/10 border border-nova-green/30 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-300 mb-2">
            Please check your inbox and click the verification link to complete your registration.
          </p>
          <p className="text-sm text-nova-green font-semibold">
            📬 Don't see the email? Check your spam/junk folder!
          </p>
        </div>

        {/* Auto-check indicator */}
        <div className="text-center text-xs text-gray-500 mb-4">
          <p>✓ Will automatically detect when you verify your email</p>
          <p className="mt-1 text-gray-600">(Just switch back to this tab after clicking the verification link)</p>
        </div>

        {/* Success Message */}
        {message && (
          <div className="text-green-400 text-sm text-center bg-green-900/20 p-3 rounded-lg border border-green-500/30 mb-4">
            {message}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="text-red-400 text-sm text-center bg-red-900/20 p-3 rounded-lg border border-red-500/30 mb-4">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleCheckVerification}
            disabled={loading}
            className="w-full bg-gradient-primary text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all transform hover:scale-105 disabled:opacity-50"
          >
            {loading ? 'Checking...' : 'I\'ve Verified My Email'}
          </button>

          <button
            onClick={handleResendEmail}
            disabled={loading || resendCooldown > 0}
            className="w-full bg-nova-bg border-2 border-nova-green/30 text-white py-3 rounded-lg font-semibold hover:bg-nova-green/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Sending...' : resendCooldown > 0 ? `Wait ${resendCooldown}s to resend` : 'Resend Verification Email'}
          </button>

          <button
            onClick={onSignOut}
            disabled={loading}
            className="w-full bg-nova-bg border border-nova-green/20 text-gray-400 py-3 rounded-lg font-semibold hover:bg-nova-bg/50 hover:text-white transition-all disabled:opacity-50"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPrompt;
